import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useCockpitExplorer } from '../hooks/useCockpitExplorer';
import { usePhaseList } from '../hooks/usePhaseList';
import { useIsMobile } from '../hooks/useIsMobile';
import { useViewportSize } from '../hooks/useViewportSize';
import { useSidebar } from '../contexts/SidebarContext';
import CockpitMarkerStage from '../components/cockpit/CockpitMarkerStage';
import CockpitControlDetail from '../components/cockpit/CockpitControlDetail';
import CockpitChecklistStepsCard from '../components/cockpit/CockpitChecklistStepsCard';
import CockpitStepsBar from '../components/cockpit/CockpitStepsBar';

const DETAIL_PANEL_WIDTH = 380;
const SIDEBAR_WIDTH = 268;
const MIN_IMAGE_WIDTH = 480;

const CockpitExplorerPage = () => {
  const { zone: zoneId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { width: viewportWidth } = useViewportSize();
  const { isOpen: isSidebarOpen } = useSidebar();
  const { data, loading } = useCockpitExplorer();
  const { slugByTitle } = usePhaseList();

  // Stack the image above the detail card (instead of a side-by-side grid)
  // whenever the remaining width after the left sidebar + 380px detail
  // panel would leave the image uncomfortably small. Computed from the
  // live viewport width, not a fixed device-type breakpoint, so it
  // re-evaluates correctly on resize/orientation change.
  const sidebarSpace = viewportWidth >= 1024 && isSidebarOpen ? SIDEBAR_WIDTH : 0;
  const availableForGrid = viewportWidth - sidebarSpace - DETAIL_PANEL_WIDTH - 40;
  const stackedLayout = availableForGrid < MIN_IMAGE_WIDTH;

  const zone = useMemo(() => (data?.zones || []).find((z) => z.id === zoneId), [data, zoneId]);

  useEffect(() => {
    if (!loading && data && !zone) {
      navigate('/cockpit', { replace: true });
    }
  }, [loading, data, zone, navigate]);

  const viewParam = searchParams.get('view');
  const view = useMemo(() => {
    if (!zone) return null;
    return zone.views.find((v) => v.id === viewParam) || zone.views[0];
  }, [zone, viewParam]);

  const viewControls = useMemo(() => {
    if (!data || !view) return [];
    return data.controls.filter((c) => c.view === view.id);
  }, [data, view]);

  const controlParam = searchParams.get('control');
  const [deselected, setDeselected] = useState(false);
  const selectedControl = useMemo(() => {
    if (deselected) return null;
    if (!viewControls.length) return null;
    return viewControls.find((c) => c.id === controlParam) || viewControls[0];
  }, [viewControls, controlParam, deselected]);

  // Optional multi-step "from your checklist" context, carried via navigation state
  const stepsContext = location.state?.steps ? location.state : null;

  const handleZonePick = (newZoneId) => {
    setDeselected(false);
    navigate(`/cockpit/${newZoneId}`);
  };

  const handleViewPick = (newViewId) => {
    setDeselected(false);
    setSearchParams({ view: newViewId }, { state: location.state });
  };

  const handleControlSelect = (controlId) => {
    setDeselected(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', view.id);
      next.set('control', controlId);
      return next;
    }, { state: location.state });
  };

  const handleDeselect = () => setDeselected(true);

  const handleStepClick = (step) => {
    setDeselected(false);
    if (step.zoneId !== zoneId) {
      navigate(`/cockpit/${step.zoneId}?view=${step.viewId}&control=${step.control.id}`, {
        state: location.state,
      });
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', step.viewId);
        next.set('control', step.control.id);
        return next;
      }, { state: location.state });
    }
  };

  if (!data || !zone || !view) return <DashboardLayout>{null}</DashboardLayout>;

  const zones = data.zones;

  return (
    <DashboardLayout>
      <div className="flex flex-col h-full min-h-0 gap-3.5" data-testid="cockpit-explorer-page">
        {/* Header: back + zone title + zone tabs */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link
              to={location.state?.returnTo || '/cockpit'}
              className="h-10 px-3 rounded-gc-button border flex items-center gap-1.5 text-sm no-underline flex-shrink-0"
              style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
              data-testid="cockpit-back-link"
            >
              <ChevronLeft className="w-4 h-4" /> {location.state?.returnTo ? 'Back' : 'Cockpit'}
            </Link>
            <h1 className="font-barlow-condensed font-bold text-2xl md:text-[34px] leading-none m-0" style={{ color: 'var(--gc-text-main)' }}>
              {zone.name}
            </h1>
          </div>

          {!isMobile && (
            <div
              role="tablist"
              aria-label="Cockpit zone"
              className="flex gap-1 rounded-gc-card border p-1 overflow-x-auto max-w-full"
              style={{ background: 'var(--gc-checklist-card)', borderColor: 'var(--gc-border-card)' }}
              data-testid="cockpit-zone-tabs"
            >
              {zones.map((z) => {
                const on = z.id === zone.id;
                return (
                  <button
                    key={z.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => handleZonePick(z.id)}
                    className="flex-shrink-0 h-9 px-3.5 rounded-gc-button font-barlow-condensed font-semibold text-[15px] cursor-pointer border-0 whitespace-nowrap"
                    style={on ? { background: 'var(--gc-cyan)', color: '#04161C' } : { background: 'transparent', color: 'var(--gc-text-muted)' }}
                    data-testid={`cockpit-zone-tab-${z.id}`}
                  >
                    {z.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {isMobile && (
          <div role="tablist" aria-label="Cockpit zone" className="flex gap-1.5 overflow-x-auto pb-1" data-testid="cockpit-zone-tabs-mobile">
            {zones.map((z) => {
              const on = z.id === zone.id;
              return (
                <button
                  key={z.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => handleZonePick(z.id)}
                  className="flex-shrink-0 h-9 px-3.5 rounded-full border font-barlow-condensed font-semibold text-[15px] cursor-pointer"
                  style={on ? { background: 'var(--gc-cyan)', color: '#04161C', borderColor: 'var(--gc-cyan)' } : { background: 'var(--gc-checklist-card)', color: 'var(--gc-text-muted)', borderColor: 'var(--gc-border-card)' }}
                  data-testid={`cockpit-zone-tab-mobile-${z.id}`}
                >
                  {z.name}
                </button>
              );
            })}
          </div>
        )}

        {/* View pills (only when zone has multiple views) */}
        {zone.views.length > 1 && (
          <div className="flex gap-1.5 flex-wrap" data-testid="cockpit-view-pills">
            {zone.views.map((v) => {
              const on = v.id === view.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => handleViewPick(v.id)}
                  className="h-8 px-3 rounded-full border font-plex-mono text-[11px] tracking-[0.06em] cursor-pointer"
                  style={on ? { background: 'var(--gc-current-row)', color: 'var(--gc-cyan)', borderColor: 'var(--gc-cyan)' } : { background: 'var(--gc-checklist-card)', color: 'var(--gc-text-muted)', borderColor: 'var(--gc-border-card)' }}
                  data-testid={`cockpit-view-pill-${v.id}`}
                >
                  {v.name}
                </button>
              );
            })}
          </div>
        )}

        {stackedLayout ? (
          <>
            {stepsContext && (
              <CockpitStepsBar
                itemText={stepsContext.itemText}
                phaseTitle={stepsContext.phaseTitle}
                steps={stepsContext.steps}
                selectedControlId={selectedControl?.id}
                onStepClick={handleStepClick}
              />
            )}
            <div className="flex-shrink-0">
              <CockpitMarkerStage
                view={view}
                controls={viewControls}
                selectedControlId={selectedControl?.id}
                onSelect={handleControlSelect}
                onDeselect={handleDeselect}
                highlightIds={stepsContext ? stepsContext.steps.map((s) => s.control.id) : []}
                compact
                fitContent
              />
            </div>
            <section
              className="flex-shrink-0 rounded-t-2xl border-t p-4 flex flex-col gap-2.5 max-h-[55vh] overflow-y-auto"
              style={{ background: 'var(--gc-checklist-card)', borderColor: 'var(--gc-border-card)' }}
              data-testid="cockpit-mobile-sheet"
            >
              <div className="self-center w-10 h-1 rounded-full" style={{ background: '#2E3A4D' }} />
              <CockpitControlDetail control={selectedControl} slugByTitle={slugByTitle} />
              {selectedControl && (
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      const i = viewControls.findIndex((c) => c.id === selectedControl.id);
                      const prev = viewControls[(i - 1 + viewControls.length) % viewControls.length];
                      handleControlSelect(prev.id);
                    }}
                    className="flex-1 h-11 rounded-gc-button border font-barlow-condensed font-semibold text-base cursor-pointer"
                    style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
                    data-testid="cockpit-prev-control"
                  >
                    ‹ Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const i = viewControls.findIndex((c) => c.id === selectedControl.id);
                      const next = viewControls[(i + 1) % viewControls.length];
                      handleControlSelect(next.id);
                    }}
                    className="flex-1 h-11 rounded-gc-button border font-barlow-condensed font-semibold text-base cursor-pointer"
                    style={{ background: '#111A26', borderColor: '#2F5C6B', color: 'var(--gc-text-main)' }}
                    data-testid="cockpit-next-control"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </section>
          </>
        ) : (
          <div className="flex-1 min-h-0 grid gap-5" style={{ gridTemplateColumns: 'minmax(0, 1fr) 380px' }}>
            <div className="min-h-0 flex flex-col gap-2.5">
              {stepsContext && (
                <CockpitStepsBar
                  itemText={stepsContext.itemText}
                  phaseTitle={stepsContext.phaseTitle}
                  steps={stepsContext.steps}
                  selectedControlId={selectedControl?.id}
                  onStepClick={handleStepClick}
                />
              )}
              <CockpitMarkerStage
                view={view}
                controls={viewControls}
                selectedControlId={selectedControl?.id}
                onSelect={handleControlSelect}
                onDeselect={handleDeselect}
                highlightIds={stepsContext ? stepsContext.steps.map((s) => s.control.id) : []}
              />
            </div>
            <div className="min-h-0 flex flex-col gap-3 overflow-y-auto">
              {stepsContext && (
                <CockpitChecklistStepsCard
                  itemText={stepsContext.itemText}
                  phaseTitle={stepsContext.phaseTitle}
                  steps={stepsContext.steps}
                  selectedControlId={selectedControl?.id}
                  onStepClick={handleStepClick}
                />
              )}
              <CockpitControlDetail control={selectedControl} slugByTitle={slugByTitle} />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CockpitExplorerPage;
